import React, { useRef, useState } from "react";

import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import TodayOutlinedIcon from "@mui/icons-material/TodayOutlined";
import { Box, Button, Chip, Popover, Stack, TextField, Typography } from "@mui/material";
import { SelectAutocomplete, SelectDateRange } from "components/forms";
import { GroupByCurrency } from "components/reports/Group-by-currency/GroupByCurrency";
import { GroupByMenu } from "components/reports/Group-by-menu/GroupByMenu";
import { useReportsQueriesSelector } from "store";
import type { CardFilters, DateRangeValue, ReportsQueries } from "store/types";

import { useFilterOptions } from "../../hooks/useFilterOptions";
import { SectionLabel } from "./SectionLabel";

interface Props {
  cardFilters?: CardFilters;
  onUpdate: (f: CardFilters | undefined) => void;
}

type FilterKey = "investors" | "assetClasses";

const FILTER_CHIPS: { key: FilterKey; label: string }[] = [
  { key: "investors", label: "Investor" },
  { key: "assetClasses", label: "Asset Class" }
];

const isEmptyFilters = (f: CardFilters): boolean =>
  f.investors.length === 0 && f.assetClasses.length === 0 && !f.currency && !f.exchangeMethod && !f.dateRange;

// Match the outlined look of AutocompleteMultiSelect (small size, theme defaults).
const CONTROL_HEIGHT = "38px";
const CONTROL_RADIUS = "8px";
const BORDER_COLOR = "rgba(0, 0, 0, 0.23)";
const BORDER_COLOR_HOVER = "rgba(0, 0, 0, 0.87)";
const ICON_COLOR = "rgba(0, 0, 0, 0.54)";

export const CardFilterSettings: React.FC<Props> = ({ cardFilters, onUpdate }) => {
  const { investorOptions, assetClassOptions } = useFilterOptions();
  const globalQueries = useReportsQueriesSelector();
  const filters: CardFilters = cardFilters ?? { investors: [], assetClasses: [] };
  const hasFilters = !isEmptyFilters(filters);

  const [editKey, setEditKey] = useState<FilterKey | null>(null);
  const [editValues, setEditValues] = useState<string[]>([]);
  const anchorRef = useRef<HTMLElement | null>(null);

  const optionsMap: Record<FilterKey, string[]> = {
    investors: investorOptions,
    assetClasses: assetClassOptions
  };

  const updateFilters = (next: CardFilters) => {
    onUpdate(isEmptyFilters(next) ? undefined : next);
  };

  const openFilter = (e: React.MouseEvent<HTMLElement>, key: FilterKey) => {
    anchorRef.current = e.currentTarget;
    setEditKey(key);
    setEditValues(filters[key]);
  };

  const handleSave = () => {
    if (!editKey) return;
    updateFilters({ ...filters, [editKey]: editValues });
    setEditKey(null);
  };

  const handleClose = () => {
    setEditKey(null);
    setEditValues([]);
  };

  const handleDelete = (key: FilterKey) => {
    updateFilters({ ...filters, [key]: [] });
  };

  const handleDateRangeChange = (value: DateRangeValue) => {
    updateFilters({ ...filters, dateRange: value });
  };

  // Reuse the global currency selector UI (GroupByMenu + GroupByCurrency), but commit to the local card filters.
  const effectiveCurrency = filters.currency ?? globalQueries.currency;
  const effectiveHedging = filters.exchangeMethod ?? globalQueries.exchangeMethod;
  const currencyDefaults: Partial<ReportsQueries> = {
    currency: effectiveCurrency,
    exchangeMethod: effectiveHedging
  };

  const handleCurrencyOkay = (values: Partial<ReportsQueries>) => {
    updateFilters({
      ...filters,
      currency: values.currency ?? effectiveCurrency,
      exchangeMethod: values.exchangeMethod ?? effectiveHedging
    });
  };

  const handleCurrencyReset = () => {
    updateFilters({ ...filters, currency: undefined, exchangeMethod: undefined });
  };

  const editLabel = FILTER_CHIPS.find((c) => c.key === editKey)?.label || "";

  return (
    <Box>
      <SectionLabel>Card Filters</SectionLabel>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: "6px", alignItems: "center" }}>
        {FILTER_CHIPS.map(({ key, label }) => {
          const count = filters[key].length;
          return (
            <Chip
              key={key}
              size="small"
              sx={{ height: "26px", fontSize: 12, color: "#0a3142" }}
              label={count > 0 ? `${label} (${count})` : label}
              onClick={(e) => openFilter(e, key)}
              {...(count > 0 ? { onDelete: () => handleDelete(key) } : {})}
            />
          );
        })}
        {(filters.investors.length > 0 || filters.assetClasses.length > 0) && (
          <Button
            size="sm"
            sx={{ color: "#26A6AE", fontSize: 11, minWidth: 0, p: "2px 6px" }}
            onClick={() => updateFilters({ ...filters, investors: [], assetClasses: [] })}
          >
            Clear All
          </Button>
        )}
      </Box>

      <Box sx={{ mt: "12px" }}>
        <SelectDateRange
          formControlProps={{ sx: { minWidth: "100%", width: "100%" } }}
          selectProps={{
            startAdornment: <TodayOutlinedIcon sx={{ fontSize: "15px", color: ICON_COLOR }} />,
            sx: {
              height: CONTROL_HEIGHT,
              borderRadius: CONTROL_RADIUS,
              backgroundColor: "#FFFFFF",
              fontSize: 16,
              color: "#0a3142",
              "&.MuiInputBase-root": { height: CONTROL_HEIGHT, paddingRight: "32px" },
              ".MuiSelect-icon": { color: "#0a3142", right: "12px" },
              ".MuiOutlinedInput-notchedOutline": { borderColor: BORDER_COLOR }
            }
          }}
          label=""
          value={filters.dateRange ?? globalQueries.dateRange}
          onChange={handleDateRangeChange}
        />
      </Box>

      <Box sx={{ mt: "8px" }}>
        <GroupByMenu
          onOkay={handleCurrencyOkay}
          onReset={handleCurrencyReset}
          renderTrigger={({ onClick }) => (
            <Button
              onClick={onClick}
              endIcon={<ArrowDropDownIcon />}
              sx={{
                height: CONTROL_HEIGHT,
                width: "100%",
                minWidth: 0,
                justifyContent: "space-between",
                textTransform: "none",
                fontSize: 16,
                fontWeight: 400,
                color: "#0a3142",
                bgcolor: "#FFFFFF",
                border: `1px solid ${BORDER_COLOR}`,
                borderRadius: CONTROL_RADIUS,
                pl: "14px",
                pr: "9px",
                "&:hover": { bgcolor: "#FFFFFF", borderColor: BORDER_COLOR_HOVER },
                "& .MuiButton-endIcon": { color: "#0a3142", marginLeft: "4px", marginRight: "2px" }
              }}
            >
              {effectiveCurrency}
            </Button>
          )}
        >
          <GroupByCurrency defaultQueries={currencyDefaults} />
        </GroupByMenu>
      </Box>

      {!hasFilters && <Typography sx={{ fontSize: 11, color: "#D1D5DB", mt: "8px" }}>Using global filters</Typography>}

      <Popover
        open={!!editKey}
        anchorEl={anchorRef.current}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        onClose={handleClose}
        disableEnforceFocus
        disableAutoFocus
      >
        <Stack sx={{ alignItems: "stretch", p: "30px", gap: "30px", maxWidth: "500px" }}>
          <Stack
            sx={{ borderBottom: "1px solid #dae0e2", pb: "5px" }}
            flexDirection="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography sx={{ fontSize: "15px" }}>{editLabel}</Typography>
            <Stack flexDirection="row">
              <Button sx={{ color: "#26A6AE" }} variant="text" onClick={handleClose}>
                Cancel
              </Button>
              <Button
                sx={{ color: "#26A6AE", ":disabled": { backgroundColor: "#FFF", color: "#bac7ce" } }}
                variant="text"
                onClick={handleSave}
              >
                Save
              </Button>
            </Stack>
          </Stack>
          <SelectAutocomplete<string, true>
            multiple
            disableCloseOnSelect
            selectAll
            limitTags={1}
            openOnFocus
            options={editKey ? optionsMap[editKey] : []}
            renderInput={(params) => <TextField {...params} label={editLabel} />}
            renderTags={(tagValue, getTagProps) => (
              <Box sx={{ maxHeight: "160px", overflowY: "auto", overflowX: "hidden" }}>
                {tagValue.map((option, index) => (
                  <Chip {...getTagProps({ index })} key={index} label={option} />
                ))}
              </Box>
            )}
            sx={{ maxWidth: "300px", minWidth: "300px" }}
            value={editValues}
            setValue={(value) => setEditValues(value)}
          />
        </Stack>
      </Popover>
    </Box>
  );
};
